import { lazy, Suspense, useEffect, useState } from "react";
import { CircleArrowDown, Gauge, Hand, Maximize2, Monitor, Moon } from "lucide-react";
import { Display, HermesError, ScreenAdvice, hostStats, type DisplayStatus, type HostStats } from "@timibot/core";
import { useApp } from "../../context";
import { Dialog, PillButton, SectionTitle, Spinner } from "../../ui/components";
// noVNC 有 200 KB：打开查看器时才加载
const BotScreenViewer = lazy(() => import("./BotScreenViewer").then((m) => ({ default: m.BotScreenViewer })));
import "./screen.css";

/**
 * 工作台最上面的“Bot 的电脑”（对应 iOS 的 BotScreenCard）：Hermes 原生的 Bot Desktop。
 * 主人可以安装、开关、接管，还能看到主机的 CPU / 内存 / 磁盘；访客只能看画面。
 */
export function BotScreenCard() {
  const app = useApp();
  const [status, setStatus] = useState<DisplayStatus>();
  const [unavailable, setUnavailable] = useState<string>();
  const [thumbnail, setThumbnail] = useState<string>();
  const [busy, setBusy] = useState(false);
  const [installing, setInstalling] = useState(false);
  const [error, setError] = useState<string>();
  const [showViewer, setShowViewer] = useState(false);
  const [confirmLow, setConfirmLow] = useState(false);
  const [sudo, setSudo] = useState("");
  const [stats, setStats] = useState<HostStats>();
  const [hidden, setHidden] = useState(false);
  const owner = !app.isGuest;
  const connected = app.connection.kind === "connected";
  const title = `${app.profiles.title(app.selectedProfile)} 的电脑`;

  const load = async () => {
    if (!app.api || !connected) return;
    try {
      const s = await Display.status(app.api, app.selectedProfile);
      setStatus(s);
      setUnavailable(undefined);
      if (s.installed) setInstalling(false);
    } catch (e) {
      if (e instanceof HermesError && e.isMethodNotFound) { setStatus(undefined); setUnavailable("更新 Hermes 后可用"); if (!owner) setHidden(true); return; }
      if (!status) setUnavailable("暂时读不到状态");
      if (!owner) setHidden(true); // 主人的 connector 还没升级到 1.9：访客不显示这张卡片
    }
  };

  useEffect(() => { void load(); }, [app.workspaceScope, app.displayRevision, connected]); // eslint-disable-line react-hooks/exhaustive-deps

  // 主机状态 15 秒刷新一次（只有主人）
  useEffect(() => {
    if (!owner || !connected || !app.api) return;
    const api = app.api;
    let stopped = false;
    const tick = () => hostStats(api).then((s) => { if (!stopped) setStats(s); }, () => {});
    void tick();
    const t = setInterval(tick, 15000);
    return () => { stopped = true; clearInterval(t); };
  }, [app.workspaceScope, connected]); // eslint-disable-line react-hooks/exhaustive-deps

  // 开着的时候每 3 秒换一张缩略图（打开全屏查看时停）
  useEffect(() => {
    if (!status?.running || showViewer || !app.api) return;
    const api = app.api;
    let stopped = false;
    const tick = () => Display.thumbnail(api, app.selectedProfile).then((t) => {
      if (stopped) return;
      if (t) setThumbnail(t); else if (status.lease?.holder === "human") setThumbnail(undefined);
    }, () => {});
    void tick();
    const t = setInterval(tick, 3000);
    return () => { stopped = true; clearInterval(t); };
  }, [status?.running, showViewer, app.workspaceScope]); // eslint-disable-line react-hooks/exhaustive-deps

  if (hidden) return null;

  const statsText = (() => {
    if (!owner || !stats) return undefined;
    const parts: string[] = [];
    if (stats.cpu_percent !== undefined) parts.push(`CPU ${Math.round(stats.cpu_percent)}%` + (stats.cpu_count ? `（${stats.cpu_count} 核）` : ""));
    else if (stats.load_avg?.[0] !== undefined && stats.cpu_count) parts.push(`CPU 负载 ${stats.load_avg[0].toFixed(1)}/${stats.cpu_count} 核`);
    if (stats.memory?.used && stats.memory.total) parts.push(`内存 ${ScreenAdvice.gb(Math.round(stats.memory.used / 1048576))}/${ScreenAdvice.gb(Math.round(stats.memory.total / 1048576))}`);
    if (stats.disk?.percent !== undefined) parts.push(`磁盘 ${Math.round(stats.disk.percent)}%`);
    return parts.length ? parts.join(" · ") : undefined;
  })();
  const advice = (s: DisplayStatus) => statsText ? (ScreenAdvice.isLowMemory(s) ? "配置偏低，不建议开启" : "可以开启") : ScreenAdvice.short(s);

  const install = async () => {
    if (!app.api) return;
    setBusy(true); setError(undefined);
    app.resetDisplayInstallLog();
    try { await Display.install(app.api, app.selectedProfile); setInstalling(true); }
    catch (e) { setError(`没能开始安装：${e instanceof Error ? e.message : String(e)}`); }
    finally { setBusy(false); }
  };
  const start = async () => {
    if (!app.api) return;
    setBusy(true); setError(undefined);
    try { setStatus(await Display.start(app.api, app.selectedProfile)); }
    catch (e) { setError(`没能开启：${ScreenAdvice.blocker(e instanceof Error ? e.message : String(e))}`); await load(); }
    finally { setBusy(false); }
  };
  const stop = async () => {
    if (!app.api) return;
    setBusy(true); setError(undefined);
    // 有人接管着也关：点“关闭”就是要关
    try { await Display.stop(app.api, app.selectedProfile, true); setThumbnail(undefined); }
    catch (e) { setError(`没能关闭：${e instanceof Error ? e.message : String(e)}`); }
    finally { setBusy(false); await load(); }
  };

  const action = busy || installing ? <Spinner /> : !unavailable && status?.supported ? (
    status.running ? <PillButton title="关闭" onClick={() => void stop()} />
      : !status.installed ? <PillButton title="安装" prominent onClick={() => void install()} />
      : <PillButton title="开启" prominent disabled={!!status.blocker} onClick={() => { if (ScreenAdvice.isLowMemory(status)) setConfirmLow(true); else void start(); }} />
  ) : undefined;

  let content;
  if (unavailable) content = <Line icon={<Monitor size={16} />} text={unavailable} />;
  else if (!status) content = <div className="center-pad"><Spinner /></div>;
  else if (!status.supported) content = <Line icon={<Monitor size={16} />} text="仅支持 Linux 主机" />;
  else if (!owner && !status.running) content = <Line icon={<Moon size={16} />} text="主人还没开启这台电脑" />;
  else if (installing) content = <Line icon={<CircleArrowDown size={16} />} text="正在安装桌面组件…" detail={app.displayInstallLog[app.displayInstallLog.length - 1]} mono />;
  else if (!status.installed) content = <Line icon={<CircleArrowDown size={16} />} text="看 Bot 操作电脑，首次使用需安装" detail={advice(status)} warning={ScreenAdvice.isLowMemory(status)} stats={statsText} />;
  else if (status.running) {
    content = (
      <>
        <button className="screen-thumb" onClick={() => setShowViewer(true)}>
          {thumbnail ? <img src={thumbnail} alt="" /> : status.lease?.holder !== "human" ? <Spinner /> : null}
          {status.lease?.holder === "human" && <span className="screen-badge top"><Hand size={12} /> 有人在操控</span>}
          <span className="screen-badge"><Maximize2 size={12} /> 全屏</span>
        </button>
        {statsText && <span className="screen-stats t-caption t-2"><Gauge size={13} /> {statsText}</span>}
      </>
    );
  } else {
    content = <Line icon={<Moon size={16} />} text="已关闭" detail={status.blocker ? `现在开不了：${ScreenAdvice.blocker(status.blocker)}` : advice(status)}
      warning={!!status.blocker || ScreenAdvice.isLowMemory(status)} stats={statsText} />;
  }

  return (
    <section>
      <SectionTitle title={title} trailing={owner ? action : undefined} />
      <div className="ws-pad16 col" style={{ gap: 10 }}>
        {content}
        {error && <span className="t-caption t-danger">{error}</span>}
      </div>
      {showViewer && app.api && (
        <Suspense fallback={null}>
          <BotScreenViewer api={app.api} profile={app.selectedProfile} title={title} canControl={owner} onClose={() => { setShowViewer(false); void load(); }} />
        </Suspense>
      )}
      <Dialog open={confirmLow} title="这台主机配置偏低"
        message={`${ScreenAdvice.memoryLine(status)}。开桌面和浏览器要多占 0.5～1.5 GB，可能拖慢 Bot。建议 4 GB 以上内存再开。`}
        onClose={() => setConfirmLow(false)}
        buttons={[{ title: "取消", role: "cancel" }, { title: "仍然开启", onClick: () => void start() }]} />
      <Dialog open={app.displaySudoRequest !== undefined} title="安装需要 sudo 密码" message="只用这一次，不会保存。"
        input={{ value: sudo, onChange: setSudo, placeholder: "这台主机的登录密码", secure: true }}
        onClose={() => { setSudo(""); setInstalling(false); app.answerDisplaySudo(undefined); }}
        buttons={[
          { title: "取消", role: "cancel" },
          { title: "安装", onClick: () => { const v = sudo; setSudo(""); app.answerDisplaySudo(v); } },
        ]} />
    </section>
  );
}

function Line({ icon, text, detail, warning, mono, stats }: { icon: React.ReactNode; text: string; detail?: string; warning?: boolean; mono?: boolean; stats?: string }) {
  return (
    <div className="screen-line">
      <span className="t-2">{icon}</span>
      <span className="col" style={{ gap: 3, flex: 1, minWidth: 0 }}>
        <span className="t-subheadline">{text}</span>
        {detail && <span className={`clamp2 ${mono ? "t-caption2 mono" : "t-caption"}`} style={{ color: warning ? "var(--warning)" : "var(--text-2)" }}>{detail}</span>}
        {stats && <span className="screen-stats t-caption t-2"><Gauge size={13} /> {stats}</span>}
      </span>
    </div>
  );
}
